(() => {
  const namespaceKey = 'GuruShopHeaderDesktopSticky';
  const markerSelector = 'script[data-header-desktop-sticky-root]';
  const sourceScript = document.currentScript;
  const requestedRootId = sourceScript?.dataset.headerDesktopStickyRoot;
  const requestedSpacerId = sourceScript?.dataset.headerDesktopStickySpacer;
  const existingApi = window[namespaceKey];

  if (existingApi) {
    existingApi.initById(requestedRootId, requestedSpacerId);
    return;
  }

  const initializedRoots = new WeakSet();
  const cleanupByRoot = new WeakMap();

  const initRoot = (sectionRoot, spacer) => {
    if (
      !(sectionRoot instanceof HTMLElement)
      || !(spacer instanceof HTMLElement)
      || initializedRoots.has(sectionRoot)
      || sectionRoot.dataset.desktopStickyBound === 'true'
    ) {
      return false;
    }

    const header = sectionRoot.querySelector('.header');

    if (!header || !sectionRoot.parentNode) {
      return false;
    }

    if (spacer.parentElement === sectionRoot) {
      sectionRoot.parentNode.insertBefore(spacer, sectionRoot.nextSibling);
    }

    const desktopMedia = window.matchMedia('(min-width: 1000px)');
    const desktopActivationOffset = 4;
    const desktopDeactivateOffset = 1;
    let frameId = null;
    let isDesktopActive = false;

    const setDesktopActive = (isActive) => {
      header.classList.toggle('header--desktop-active', isActive);
      sectionRoot.classList.toggle('header-section--desktop-fixed', isActive);
    };

    const resetDesktopState = () => {
      isDesktopActive = false;
      setDesktopActive(false);
      sectionRoot.style.removeProperty('top');
      spacer.style.height = '0px';
    };

    const resolveDesktopTopOffset = () => {
      const rootStyles = window.getComputedStyle(document.documentElement);
      const announcementHeight = Number.parseFloat(
        rootStyles.getPropertyValue('--announcement-bar-height') || '0',
      );

      if (!Number.isFinite(announcementHeight) || announcementHeight <= 0) {
        return 0;
      }

      const scrollTop = Math.max(0, window.scrollY || window.pageYOffset || 0);
      return Math.max(0, announcementHeight - scrollTop);
    };

    const syncDesktopState = () => {
      if (!desktopMedia.matches) {
        resetDesktopState();
        frameId = null;
        return;
      }

      const scrollTop = Math.max(0, window.scrollY || window.pageYOffset || 0);
      const shouldBeActive = isDesktopActive
        ? scrollTop > desktopDeactivateOffset
        : scrollTop > desktopActivationOffset;

      if (shouldBeActive !== isDesktopActive) {
        isDesktopActive = shouldBeActive;
        setDesktopActive(isDesktopActive);
      }

      if (isDesktopActive) {
        sectionRoot.style.setProperty('top', `${resolveDesktopTopOffset()}px`, 'important');
        spacer.style.height = `${sectionRoot.offsetHeight}px`;
      } else {
        sectionRoot.style.removeProperty('top');
        spacer.style.height = '0px';
      }

      frameId = null;
    };

    const scheduleSync = () => {
      if (frameId !== null) return;
      frameId = window.requestAnimationFrame(syncDesktopState);
    };

    window.addEventListener('pageshow', syncDesktopState, { passive: true });
    window.addEventListener('resize', scheduleSync, { passive: true });
    window.addEventListener('scroll', scheduleSync, { passive: true });

    if (desktopMedia.addEventListener) {
      desktopMedia.addEventListener('change', scheduleSync);
    } else if (desktopMedia.addListener) {
      desktopMedia.addListener(scheduleSync);
    }

    sectionRoot.dataset.desktopStickyBound = 'true';
    initializedRoots.add(sectionRoot);
    cleanupByRoot.set(sectionRoot, () => {
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
        frameId = null;
      }

      window.removeEventListener('pageshow', syncDesktopState);
      window.removeEventListener('resize', scheduleSync);
      window.removeEventListener('scroll', scheduleSync);

      if (desktopMedia.removeEventListener) {
        desktopMedia.removeEventListener('change', scheduleSync);
      } else if (desktopMedia.removeListener) {
        desktopMedia.removeListener(scheduleSync);
      }

      resetDesktopState();

      if (spacer.parentElement !== sectionRoot) {
        sectionRoot.append(spacer);
      }

      delete sectionRoot.dataset.desktopStickyBound;
    });

    syncDesktopState();
    return true;
  };

  const initById = (rootId, spacerId) => {
    if (!rootId) return false;

    const sectionRoot = document.getElementById(rootId);
    const marker = Array.from(document.querySelectorAll(markerSelector)).find(
      (candidate) => candidate.dataset.headerDesktopStickyRoot === rootId,
    );
    const resolvedSpacerId = spacerId || marker?.dataset.headerDesktopStickySpacer;

    return initRoot(sectionRoot, resolvedSpacerId ? document.getElementById(resolvedSpacerId) : null);
  };

  const cleanupRoot = (sectionRoot) => {
    if (!(sectionRoot instanceof HTMLElement) || !initializedRoots.has(sectionRoot)) return false;

    cleanupByRoot.get(sectionRoot)?.();
    cleanupByRoot.delete(sectionRoot);
    initializedRoots.delete(sectionRoot);
    return true;
  };

  const initWithin = (scope) => {
    if (!(scope instanceof Element || scope instanceof Document)) return;

    if (scope.matches?.(markerSelector)) {
      initById(scope.dataset.headerDesktopStickyRoot, scope.dataset.headerDesktopStickySpacer);
    }

    scope.querySelectorAll(markerSelector).forEach((marker) => {
      initById(marker.dataset.headerDesktopStickyRoot, marker.dataset.headerDesktopStickySpacer);
    });
  };

  window[namespaceKey] = { initById, initWithin, cleanupRoot };

  document.addEventListener('shopify:section:load', (event) => {
    initWithin(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    cleanupRoot(event.target);
  });

  initById(requestedRootId, requestedSpacerId);
})();
